import {
  buildSection,
  DetailTable,
  EmptyDetail,
  formatCurrency,
  humanizeEnum,
  yesNo,
  type DetailSection,
} from "./detailPrimitives";

// life / disability (life, add, voluntary_life, std, ltd). Renders the
// life_disability_coverage_config row plus any age-reduction schedule.
export function LifeDisabilityBenefitBlock({
  config,
  ageReductions,
}: {
  config: any;
  ageReductions: any[];
}) {
  if (!config) return <EmptyDetail />;

  const coverageAmountDisplay = (() => {
    switch (config.coverageAmountType) {
      case "flat_amount":
        return formatCurrency(config.flatCoverageAmount);
      case "earnings_multiple":
        return config.earningsMultiple != null
          ? `${Number(config.earningsMultiple)}× ${humanizeEnum(config.earningsBase)}`
          : "—";
      case "earnings_percentage":
        return config.earningsPercentage != null
          ? `${Number(config.earningsPercentage)}% of ${humanizeEnum(config.earningsBase)}`
          : "—";
      default:
        return humanizeEnum(config.coverageAmountType);
    }
  })();

  const isDisability =
    config.eliminationPeriod != null ||
    config.maximumBenefitPeriod != null ||
    config.disabilityDefinition != null ||
    config.maxMonthlyBenefit != null;

  const sortedAgeReductions = Array.isArray(ageReductions)
    ? ageReductions
        .slice()
        .sort((a: any, b: any) => Number(a.ageThreshold) - Number(b.ageThreshold))
    : [];

  const sections = [
    buildSection("Coverage", [
      { label: "Coverage Amount", value: coverageAmountDisplay },
      { label: "Minimum", value: formatCurrency(config.minimumCoverage) },
      { label: "Maximum", value: formatCurrency(config.maximumCoverage) },
      { label: "Increment", value: formatCurrency(config.coverageIncrement) },
    ]),
    buildSection("Guaranteed Issue", [
      { label: "Guaranteed Issue", value: formatCurrency(config.guaranteedIssueAmount) },
      { label: "EOI Required", value: yesNo(config.eoiRequired) },
      { label: "Max Without EOI", value: formatCurrency(config.maximumWithoutEoi) },
    ]),
    isDisability
      ? buildSection("Disability Terms", [
          { label: "Elimination Period", value: humanizeEnum(config.eliminationPeriod) },
          { label: "Max Benefit Period", value: humanizeEnum(config.maximumBenefitPeriod) },
          { label: "Definition", value: humanizeEnum(config.disabilityDefinition) },
          { label: "Max Monthly Benefit", value: formatCurrency(config.maxMonthlyBenefit) },
        ])
      : null,
    buildSection("Features", [
      { label: "Portability", value: yesNo(config.portabilityAllowed) },
      { label: "Conversion", value: yesNo(config.conversionAllowed) },
      config.acceleratedDeathBenefit
        ? { label: "Accelerated Death Benefit", value: "Yes" }
        : { label: "Accelerated Death Benefit", value: "—" },
    ]),
    sortedAgeReductions.length > 0
      ? buildSection(
          "Age Reduction",
          sortedAgeReductions.map((schedule: any) => ({
            label: `At age ${schedule.ageThreshold}`,
            value: `reduced to ${Number(schedule.reductionPercentage)}%`,
          })),
        )
      : null,
  ].filter(Boolean) as DetailSection[];

  return <DetailTable sections={sections} />;
}
